import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [todaySessions, setTodaySessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTodayAttendance();
    fetchSummary();

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      const response = await api.get('/attendance/today');
      const d = response.data.data;
      if (d?.sessions) {
        // New format: { sessions, current }
        setTodaySessions(d.sessions);
        setTodayAttendance(d.current || null);
      } else {
        // Old format: single attendance object
        setTodaySessions(d ? [d] : []);
        setTodayAttendance(d || null);
      }
    } catch (error) {
      console.error('Error fetching today attendance:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/attendance/summary');
      setSummary(response.data.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleClockIn = async () => {
    setLoading(true);
    try {
      const response = await api.post('/attendance/clock-in', {
        location: 'Web App',
      });
      const newSession = response.data.data;
      setTodayAttendance(newSession);
      setTodaySessions(prev => [...prev, newSession]);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to clock in');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setLoading(true);
    try {
      const response = await api.post('/attendance/clock-out', {
        location: 'Web App',
      });
      const updated = response.data.data;
      setTodayAttendance(updated);
      setTodaySessions(prev => prev.map(s => s.id === updated.id ? updated : s));
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to clock out');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('he-IL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Helper to parse MySQL date strings (handles both ISO and space-separated formats)
  const parseDateTime = (timeStr) => {
    if (!timeStr) return null;
    const normalized = String(timeStr).replace(' ', 'T');
    return new Date(normalized.endsWith('Z') ? normalized : normalized + 'Z');
  };

  const formatTimeStr = (timeStr) => {
    if (!timeStr) return '--:--';
    const date = parseDateTime(timeStr);
    return date.toLocaleTimeString('he-IL', {
      timeZone: 'Asia/Jerusalem',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getWorkingHours = () => {
    if (todaySessions.length === 0) return '0h 0m';

    let totalMinutes = 0;
    for (const session of todaySessions) {
      if (!session.clock_in) continue;
      const clockIn = parseDateTime(session.clock_in);
      const clockOut = session.clock_out ? parseDateTime(session.clock_out) : new Date();
      totalMinutes += Math.floor((clockOut - clockIn) / 1000 / 60);
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  // Convert decimal hours to HH:MM format (e.g., 17.7 -> "17:42")
  const formatHoursToHHMM = (decimalHours) => {
    if (!decimalHours && decimalHours !== 0) return '0:00';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  };

  return (
    <div className="dashboard">
      <div className="welcome-section">
        <h1>Welcome, {user?.name}!</h1>
        <p>{formatDate(currentTime)}</p>
      </div>
      <div className="dashboard-layout">
        <div className="clock-section">
          <div className="clock-ring">
            <svg className="clock-ring-svg" viewBox="0 0 200 200">
              <circle cx="100" cy="100" r="90" fill="none" stroke={todayAttendance?.clock_in && !todayAttendance?.clock_out ? 'rgba(220,38,38,0.1)' : 'rgba(0,20,120,0.08)'} strokeWidth="4" />
              <circle cx="100" cy="100" r="90" fill="none" stroke={todayAttendance?.clock_in && !todayAttendance?.clock_out ? '#dc2626' : '#001478'} strokeWidth="4"
                strokeDasharray={`${(todayAttendance?.clock_in && !todayAttendance?.clock_out
                  ? Math.min(((Date.now() - parseDateTime(todayAttendance.clock_in).getTime()) / 1000 % 60) / 60, 1)
                  : currentTime.getSeconds() / 60) * 565.48} 565.48`}
                strokeLinecap="round"
                style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transition: 'stroke-dasharray 0.3s ease, stroke 0.3s ease' }} />
            </svg>
            <div className="clock-ring-content">
              {todayAttendance?.clock_in && !todayAttendance?.clock_out ? (
                <>
                  <div className="current-time">{getWorkingHours()}</div>
                  <div className="clock-status-label">{formatTime(currentTime)}</div>
                </>
              ) : (
                <>
                  <div className="current-time">{formatTime(currentTime)}</div>
                  <div className="clock-status-label">
                    {!todayAttendance?.clock_in ? 'Ready to start' : 'Session complete'}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="clock-actions">
            {!todayAttendance?.clock_in ? (
              <button
                className="btn-clock btn-clock-in"
                onClick={handleClockIn}
                disabled={loading}
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Start Day
              </button>
            ) : !todayAttendance?.clock_out ? (
              <button
                className="btn-clock btn-clock-out"
                onClick={handleClockOut}
                disabled={loading}
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
                End Day
              </button>
            ) : (
              <button
                className="btn-clock btn-clock-in"
                onClick={handleClockIn}
                disabled={loading}
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Start New Session
              </button>
            )}
          </div>
        </div>

        <div className="dashboard-right">
          <div className="today-status">
        <div className="status-card">
          <div className="status-icon clock-in-icon">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="status-info">
            <span className="status-label">Clock In</span>
            <span className="status-value">{formatTimeStr(todayAttendance?.clock_in)}</span>
          </div>
        </div>

        <div className="status-card">
          <div className="status-icon clock-out-icon">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 8 14" />
            </svg>
          </div>
          <div className="status-info">
            <span className="status-label">Clock Out</span>
            <span className="status-value">{formatTimeStr(todayAttendance?.clock_out)}</span>
          </div>
        </div>

        <div className="status-card">
          <div className="status-icon hours-icon">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="status-info">
            <span className="status-label">Working Hours</span>
            <span className="status-value">{getWorkingHours()}</span>
          </div>
        </div>
      </div>

      {summary && (
        <div className="summary-section">
          <h2>Monthly Summary</h2>
          <div className="summary-grid">
            <div className="summary-card">
              <span className="summary-value">{summary.present_days}</span>
              <span className="summary-label">Present</span>
            </div>
            <div className="summary-card">
              <span className="summary-value">{summary.sick_days}</span>
              <span className="summary-label">Sick Leave</span>
            </div>
            <div className="summary-card">
              <span className="summary-value">{summary.vacation_days}</span>
              <span className="summary-label">Vacation</span>
            </div>
            <div className="summary-card">
              <span className="summary-value">{formatHoursToHHMM(parseFloat(summary.total_hours) || 0)}</span>
              <span className="summary-label">Total Hours</span>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
